import { cn } from '../../../lib/cn'
import { IconeCheque } from '../../../components/Icones'
import { ETAPAS, type Etapa } from './etapas'

interface Props {
  etapaAtual: Etapa
  /** Só é chamado para etapa já percorrida: a de frente exige a atual válida. */
  aoIr: (etapa: Etapa) => void
}

/**
 * A barra de cada etapa.
 *
 * O laranja institucional (#f85808) aparece aqui como preenchimento, e não como texto: sobre
 * branco ele dá ~3,4:1, abaixo dos 4,5:1 da WCAG AA. Numa barra de 4px sem texto em cima, o tom
 * vivo é exatamente o que faz "você está aqui" ser visto de longe.
 */
const COR_DA_BARRA = {
  concluida: 'bg-niteroi-700',
  atual: 'bg-laranja-500',
  futura: 'bg-slate-200',
}

const COR_DO_NUMERO = {
  concluida: 'bg-niteroi-50 text-niteroi-800 ring-1 ring-niteroi-200',
  atual: 'bg-niteroi-700 text-white',
  futura: 'bg-white text-slate-500 ring-1 ring-slate-300',
}

type Situacao = keyof typeof COR_DA_BARRA

function situacaoDe(etapa: Etapa, atual: Etapa): Situacao {
  if (etapa.numero < atual.numero) {
    return 'concluida'
  }
  return etapa.numero === atual.numero ? 'atual' : 'futura'
}

/**
 * Onde a pessoa está, quanto falta e o que já ficou para trás.
 *
 * Um formulário longo sem esta barra é o que faz o servidor leigo em TI desistir no meio: ele não
 * sabe se está no começo ou no fim, e o botão "continuar" pode tanto abrir mais três telas quanto
 * gravar. Aqui o número de etapas é dito antes de começar.
 *
 * Etapa já percorrida é botão de verdade — voltar para corrigir uma resposta não pode custar
 * refazer o caminho. Etapa da frente fica desabilitada, e não escondida: saber que existem duas
 * telas depois desta é parte de decidir se dá tempo de preencher agora.
 *
 * @param props a etapa corrente e o que fazer ao voltar para uma anterior
 * @returns o indicador de etapas
 */
export function PassosDoAssistente({ etapaAtual, aoIr }: Props) {
  return (
    <nav aria-label="Etapas do cadastro">
      <ol className="grid gap-3 sm:grid-cols-3">
        {ETAPAS.map((etapa) => {
          const situacao = situacaoDe(etapa, etapaAtual)
          const concluida = situacao === 'concluida'
          return (
            <li key={etapa.id}>
              <button
                type="button"
                disabled={!concluida}
                aria-current={situacao === 'atual' ? 'step' : undefined}
                onClick={() => aoIr(etapa)}
                className={cn(
                  'w-full rounded-lg text-left transition-colors focus-visible:outline focus-visible:outline-2',
                  'focus-visible:outline-offset-2 focus-visible:outline-niteroi-600',
                  concluida ? 'cursor-pointer hover:bg-slate-50' : 'cursor-default',
                )}
              >
                <span className={cn('block h-1 rounded-full', COR_DA_BARRA[situacao])} />
                <span className="mt-2 flex items-start gap-2 px-1 pb-1">
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                      COR_DO_NUMERO[situacao],
                    )}
                  >
                    {concluida ? <IconeCheque className="h-3.5 w-3.5" /> : etapa.numero}
                  </span>
                  <span className="min-w-0">
                    <span
                      className={cn(
                        'block text-sm font-semibold',
                        situacao === 'futura' ? 'text-slate-500' : 'text-slate-900',
                      )}
                    >
                      {/* O número vai no texto também: o círculo é `aria-hidden` e, sem ele,
                          quem usa leitor de tela ouviria três rótulos sem ordem entre eles. */}
                      <span className="sr-only">Etapa {etapa.numero} de {ETAPAS.length}: </span>
                      {etapa.rotulo}
                      {concluida && <span className="sr-only"> (preenchida)</span>}
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-500">{etapa.resumo}</span>
                  </span>
                </span>
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
